// Thin-line scientific diagrams. Stroke uses currentColor; accents use text-saffron.
// All diagrams are responsive (viewBox + w-full h-auto) and label with the mono font.

const LBL = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: 0.4 };
const LBL_SM = { fontFamily: "'JetBrains Mono', monospace", fontSize: 8.5, letterSpacing: 0.3 };

export const RetrievalFusionDiagram = ({ className = "text-bone/70" }) => (
  <svg viewBox="0 0 640 300" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Three-signal retrieval fusion">
    {[
      { y: 60, t: "SEMANTIC", d: "vector similarity" },
      { y: 150, t: "TEMPORAL", d: "recency weighting" },
      { y: 240, t: "RELATIONAL", d: "knowledge graph" },
    ].map((s, i) => (
      <g key={i}>
        <rect x="1" y={s.y - 26} width="176" height="52" stroke="currentColor" strokeOpacity="0.4" />
        <text x="16" y={s.y - 6} fill="currentColor" style={LBL}>{s.t}</text>
        <text x="16" y={s.y + 12} fill="currentColor" fillOpacity="0.5" style={LBL_SM}>{s.d}</text>
        <path d={`M177 ${s.y} C 260 ${s.y}, 300 150, 380 150`} stroke="currentColor" strokeOpacity="0.35" />
        <circle cx="177" cy={s.y} r="2.5" fill="currentColor" />
      </g>
    ))}
    <circle cx="380" cy="150" r="5" className="text-saffron" fill="currentColor" />
    <path d="M385 150 H 470" stroke="currentColor" className="text-saffron" />
    <rect x="470" y="118" width="168" height="64" className="text-saffron" stroke="currentColor" />
    <text x="486" y="146" className="text-saffron" fill="currentColor" style={LBL}>RANKED CONTEXT</text>
    <text x="486" y="164" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>one fused window</text>
  </svg>
);

export const IngestionDiagram = ({ className = "text-bone/70" }) => {
  const steps = ["INGEST", "CHUNK", "EMBED", "STORE"];
  const sub = ["content-addressed", "deterministic", "on-device", "reproducible"];
  return (
    <svg viewBox="0 0 680 130" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Deterministic ingestion pipeline">
      {steps.map((s, i) => {
        const x = 8 + i * 168;
        return (
          <g key={s}>
            <rect x={x} y="30" width="140" height="56" stroke="currentColor" strokeOpacity="0.4" />
            <text x={x + 16} y="58" fill="currentColor" style={LBL}>{`0${i + 1}`}</text>
            <text x={x + 44} y="58" fill="currentColor" style={LBL}>{s}</text>
            <text x={x + 16} y="74" fill="currentColor" fillOpacity="0.5" style={LBL_SM}>{sub[i]}</text>
            {i < steps.length - 1 && (
              <path d={`M${x + 140} 58 H ${x + 168}`} stroke="currentColor" className="text-saffron" markerEnd="url(#arr)" />
            )}
          </g>
        );
      })}
      <defs>
        <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0 1 L6 4 L0 7" stroke="currentColor" className="text-saffron" fill="none" />
        </marker>
      </defs>
    </svg>
  );
};

export const MemoryGraphDiagram = ({ className = "text-bone/70" }) => {
  const nodes = [
    { x: 120, y: 70, r: 7, a: 1 },
    { x: 300, y: 40, r: 5, a: 0.8 },
    { x: 480, y: 90, r: 6, a: 0.9 },
    { x: 240, y: 160, r: 5, a: 0.7 },
    { x: 420, y: 180, r: 7, a: 1 },
    { x: 560, y: 40, r: 4, a: 0.6 },
  ];
  const edges = [[0, 1], [1, 2], [0, 3], [3, 4], [2, 5], [4, 2], [1, 3]];
  return (
    <svg viewBox="0 0 640 230" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Local knowledge graph">
      {edges.map(([a, b], i) => (
        <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} stroke="currentColor" strokeOpacity="0.22" />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r + 6} stroke="currentColor" strokeOpacity="0.15" />
          <circle cx={n.x} cy={n.y} r={n.r} fill="currentColor" className={i === 0 || i === 4 ? "text-saffron" : ""} fillOpacity={i === 0 || i === 4 ? 1 : n.a * 0.7} />
        </g>
      ))}
      <text x="120" y="52" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>people</text>
      <text x="420" y="205" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>projects</text>
      <text x="480" y="112" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>orgs</text>
    </svg>
  );
};

export const MembraneDiagram = ({ className = "text-bone/70" }) => (
  <svg viewBox="0 0 640 240" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="The Smritium Membrane">
    <rect x="4" y="70" width="150" height="100" stroke="currentColor" strokeOpacity="0.4" />
    <text x="22" y="116" fill="currentColor" style={LBL}>LOCAL</text>
    <text x="22" y="134" fill="currentColor" fillOpacity="0.55" style={LBL_SM}>your corpus</text>

    <line x1="154" y1="120" x2="250" y2="120" stroke="currentColor" strokeOpacity="0.4" />
    {/* Membrane */}
    <rect x="250" y="30" width="150" height="180" className="text-saffron" stroke="currentColor" />
    <text x="266" y="58" className="text-saffron" fill="currentColor" style={LBL}>MEMBRANE</text>
    {["PII detection", "redaction", "human approval", "egress audit"].map((t, i) => (
      <g key={t}>
        <circle cx="270" cy={86 + i * 30} r="2.5" className="text-saffron" fill="currentColor" />
        <text x="282" y={90 + i * 30} fill="currentColor" fillOpacity="0.75" style={LBL_SM}>{t}</text>
      </g>
    ))}

    <line x1="400" y1="120" x2="490" y2="120" stroke="currentColor" strokeOpacity="0.4" strokeDasharray="4 4" />
    <rect x="490" y="70" width="146" height="100" stroke="currentColor" strokeOpacity="0.4" strokeDasharray="4 4" />
    <text x="508" y="116" fill="currentColor" style={LBL}>CLOUD</text>
    <text x="508" y="134" fill="currentColor" fillOpacity="0.55" style={LBL_SM}>optional · swappable</text>
  </svg>
);

// Memory motif — a node that persists across a fading sequence (continuity of recall).
export const PersistenceMark = ({ className = "text-bone/60" }) => (
  <svg viewBox="0 0 520 80" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Continuity of memory">
    {Array.from({ length: 7 }).map((_, i) => (
      <line key={`t${i}`} x1={40 + i * 75} y1="14" x2={40 + i * 75} y2="66" stroke="currentColor" strokeOpacity={0.08 + i * 0.02} />
    ))}
    <line x1="40" y1="40" x2="490" y2="40" stroke="currentColor" strokeOpacity="0.15" />
    {Array.from({ length: 7 }).map((_, i) => {
      const persist = i === 0 || i === 3 || i === 6;
      return (
        <circle
          key={i}
          cx={40 + i * 75}
          cy="40"
          r={persist ? 6 : 4}
          fill="currentColor"
          className={persist ? "text-saffron" : ""}
          fillOpacity={persist ? 1 : 0.25 + i * 0.05}
        />
      );
    })}
  </svg>
);

// Interlocking strata — memory as substrate.
export const StrataDiagram = ({ className = "text-bone/70" }) => (
  <svg viewBox="0 0 640 200" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Memory as substrate">
    {[
      { y: 20, t: "APPLICATIONS", o: 0.35 },
      { y: 64, t: "AGENTS & MODELS", o: 0.45 },
      { y: 108, t: "MEMORY SUBSTRATE — UPII / UEII", o: 1, accent: true },
      { y: 152, t: "YOUR DEVICE / YOUR INFRASTRUCTURE", o: 0.45 },
    ].map((r) => (
      <g key={r.t}>
        <rect x="4" y={r.y} width="632" height="36" stroke="currentColor" strokeOpacity={r.accent ? 1 : r.o} className={r.accent ? "text-saffron" : ""} />
        <text x="22" y={r.y + 23} fill="currentColor" fillOpacity={r.accent ? 1 : r.o + 0.3} className={r.accent ? "text-saffron" : ""} style={LBL}>{r.t}</text>
      </g>
    ))}
  </svg>
);

export const CitedAnswerDiagram = ({ className = "text-bone/70" }) => (
  <svg viewBox="0 0 640 200" className={`w-full h-auto ${className}`} fill="none" role="img" aria-label="Cite or abstain">
    <rect x="4" y="20" width="632" height="46" stroke="currentColor" strokeOpacity="0.4" />
    <text x="22" y="40" fill="currentColor" style={LBL}>QUERY</text>
    <text x="22" y="56" fill="currentColor" fillOpacity="0.55" style={LBL_SM}>"what did we agree on latency?"</text>
    <path d="M320 66 V 96" stroke="currentColor" strokeOpacity="0.4" markerEnd="url(#arr2)" />
    <rect x="4" y="96" width="310" height="86" className="text-signal" stroke="currentColor" />
    <text x="22" y="120" className="text-signal" fill="currentColor" style={LBL}>ANSWER · CITED</text>
    <text x="22" y="140" fill="currentColor" fillOpacity="0.7" style={LBL_SM}>[1] sync.md (chunk a3f2c9)</text>
    <text x="22" y="156" fill="currentColor" fillOpacity="0.7" style={LBL_SM}>[2] terms.md (chunk 77d01b)</text>
    <rect x="326" y="96" width="310" height="86" stroke="currentColor" strokeOpacity="0.4" />
    <text x="344" y="120" fill="currentColor" style={LBL}>LOW CONFIDENCE</text>
    <text x="344" y="140" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>→ abstain, do not invent</text>
    <text x="344" y="156" fill="currentColor" fillOpacity="0.6" style={LBL_SM}>a citation</text>
    <defs>
      <marker id="arr2" markerWidth="8" markerHeight="8" refX="4" refY="6" orient="auto">
        <path d="M1 0 L4 6 L7 0" stroke="currentColor" fill="none" />
      </marker>
    </defs>
  </svg>
);
